import { Animation } from './Animation';
import { Vector3 } from '../types';

export class WiggleAnimation extends Animation {
    // Wiggle-specific properties
    private _basePosition: Vector3 = Vector3.zero();
    private _hasStoredBasePosition: boolean = false;
    
    // --- Manual Overrides ---
    private _manualSpeed: number | null = null;
    private _manualAmplitude: number | null = null;
    
    // Wiggle axes (can be customized)
    public wiggleX: boolean = true;
    public wiggleY: boolean = true;
    public wiggleZ: boolean = false; // Usually false for 2D-like wiggle
    
    // Wiggle patterns
    public xPhaseOffset: number = 0;
    public yPhaseOffset: number = Math.PI / 3; // Offset for more natural movement
    public zPhaseOffset: number = Math.PI / 2;
    
    // Wiggle frequency multipliers (relative to base speed)
    public xFrequencyMultiplier: number = 1.0;
    public yFrequencyMultiplier: number = 1.2; // Slightly different for organic feel
    public zFrequencyMultiplier: number = 0.8;

    constructor(name: string = 'Wiggle Animation') {
        super(name, 'wiggle');
    }

    // Store the original position when animation starts
    protected onStart(): void {
        super.onStart();
        
        const target = this.getTarget();
        if (target && target.transform && !this._hasStoredBasePosition) {
            // Store the current position as base position
            this._basePosition = target.transform.position.value?.clone() || Vector3.zero();
            this._hasStoredBasePosition = true;
        }
    }

    protected onStop(): void {
        super.onStop();
        
        // Restore original position when stopping
        const target = this.getTarget();
        if (target && target.transform && this._hasStoredBasePosition) {
            target.transform.position.setImmediate(this._basePosition);
        }
    }

    protected onReset(): void {
        this._hasStoredBasePosition = false;
        this._manualSpeed = null;
        this._manualAmplitude = null;
    }

    // Main animation logic
    protected animate(elapsedTime: number, deltaTime: number): void {
        const target = this.getTarget();
        if (!target || !target.transform) return;
        const transform = target.transform;
        
        // Get live parameters from POI data, with manual override
        const amplitude = this.getAmplitude();
        const speed = this.getSpeed();
        
        // Skip animation if amplitude is zero
        if (amplitude <= 0) return;
        
        // Calculate wiggle offsets
        const wiggleOffset = this.calculateWiggleOffset(elapsedTime, amplitude, speed);
        
        // Apply wiggle to position
        const newPosition = this._basePosition.add(wiggleOffset);
        transform.position.setImmediate(newPosition);
    }

    private calculateWiggleOffset(time: number, amplitude: number, speed: number): Vector3 {
        let offsetX = 0;
        let offsetY = 0;
        let offsetZ = 0;
        
        if (this.wiggleX) {
            offsetX = this.oscillate(
                time + this.xPhaseOffset,
                speed * this.xFrequencyMultiplier,
                amplitude
            );
        }
        
        if (this.wiggleY) {
            offsetY = this.oscillate(
                time + this.yPhaseOffset,
                speed * this.yFrequencyMultiplier,
                amplitude
            );
        }
        
        if (this.wiggleZ) {
            offsetZ = this.oscillate(
                time + this.zPhaseOffset,
                speed * this.zFrequencyMultiplier,
                amplitude
            );
        }
        
        return new Vector3(offsetX, offsetY, offsetZ);
    }

    protected oscillate(time: number, frequency: number = 1, amplitude: number = 1, offset: number = 0): number {
        return Math.sin(time * frequency * Math.PI * 2) * amplitude + offset;
    }

    // Wiggle never really "completes" - it's continuous
    protected shouldCompleteLoop(elapsedTime: number): boolean {
        return false; // Continuous animation
    }

    // Wiggle pattern presets
    setWigglePattern(pattern: WigglePattern): void {
        switch (pattern) {
            case 'gentle':
                this.wiggleX = true;
                this.wiggleY = true;
                this.wiggleZ = false;
                this.xFrequencyMultiplier = 0.8;
                this.yFrequencyMultiplier = 1.0;
                this.xPhaseOffset = 0;
                this.yPhaseOffset = Math.PI / 4;
                break;
                
            case 'energetic':
                this.wiggleX = true;
                this.wiggleY = true;
                this.wiggleZ = false;
                this.xFrequencyMultiplier = 1.5;
                this.yFrequencyMultiplier = 1.8;
                this.xPhaseOffset = 0;
                this.yPhaseOffset = Math.PI / 3;
                break;
                
            case 'horizontal':
                this.wiggleX = true;
                this.wiggleY = false;
                this.wiggleZ = false;
                this.xFrequencyMultiplier = 1.0;
                break;
                
            case 'vertical':
                this.wiggleX = false;
                this.wiggleY = true;
                this.wiggleZ = false;
                this.yFrequencyMultiplier = 1.0;
                break;
                
            case 'circular':
                this.wiggleX = true;
                this.wiggleY = true;
                this.wiggleZ = false;
                this.xFrequencyMultiplier = 1.0;
                this.yFrequencyMultiplier = 1.0;
                this.xPhaseOffset = 0;
                this.yPhaseOffset = Math.PI / 2; // 90 degree phase shift for circular motion
                break;
                
            case '3d':
                this.wiggleX = true;
                this.wiggleY = true;
                this.wiggleZ = true;
                this.xFrequencyMultiplier = 1.0;
                this.yFrequencyMultiplier = 1.2;
                this.zFrequencyMultiplier = 0.8;
                this.xPhaseOffset = 0;
                this.yPhaseOffset = Math.PI / 3;
                this.zPhaseOffset = Math.PI / 2;
                break;
        }
    }

    // Reset base position (useful when POI is moved)
    resetBasePosition(): void {
        const target = this.getTarget();
        if (target && target.transform) {
            this._basePosition = target.transform.position.value?.clone() || Vector3.zero();
            this._hasStoredBasePosition = true;
        }
    }

    // Get current wiggle offset (useful for debugging)
    getCurrentWiggleOffset(): Vector3 {
        if (!this.isRunning()) return Vector3.zero();
        
        const elapsedTime = this.getElapsedTimeSeconds();
        const amplitude = this.getAmplitude();
        const speed = this.getSpeed();
        
        return this.calculateWiggleOffset(elapsedTime, amplitude, speed);
    }

    public setSpeed(speed: number): void {
        this._manualSpeed = speed;
    }

    public getSpeed(): number {
        return this._manualSpeed ?? 1;
    }

    public setAmplitude(amplitude: number): void {
        this._manualAmplitude = amplitude;
    }

    public getAmplitude(): number {
        return this._manualAmplitude ?? 0;
    }
}

// Wiggle pattern types
export type WigglePattern = 'gentle' | 'energetic' | 'horizontal' | 'vertical' | 'circular' | '3d'; 